import React, { useState, useEffect } from 'react';
import { Translate } from 'react-localize-redux';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { TableVirtuoso } from 'react-virtuoso';
import Tooltip from '../molecules/Tooltip';
import SortOff from '../../resources/svg/sort_off.svg';
import SortUp from '../../resources/svg/sort_up.svg';
import SortDown from '../../resources/svg/sort_down.svg';
import Icon, { IconSizes, IconTypes, IconVariants } from '../foundations/Icon';
import TableCell from '../widgets/TableCell';
import Checkbox from '../atoms/Checkbox';
import TextField from '../molecules/TextField';
import css from './Table.css';
import { TableWidgetMetadataShape } from '../../global/widgetTypes';
import PaginationFooter from './Table.PaginationFooter';
import GroupHeaderCell from './Table.GroupHeaderCell';

const DEFAULT_HEADER_ALIGNMENT = 'left';
const DEFAULT_METADATA = [];
const DEFAULT_DATA = [];

const handleRowClick = (e, row, rowIndex, onRowClick) => {
    let element = e.target;
    while (element && element !== e.currentTarget) {
        if (element.onclick || element.getAttribute('onClick')) {
            return;
        }
        element = element.parentElement;
    }

    if (onRowClick && !row.disabled) {
        onRowClick(row, rowIndex);
    }
};

const getRowStyle = (onRowClick, row) => ({ cursor: onRowClick && !row.disabled ? 'pointer' : 'default' });

const VirtuosoTableRow = ({ context, getRowClassName, onRowClick, ...rest }) => {
    const rowIndex = rest['data-index'];
    const item = context?.data?.[rowIndex];
    const row = item?.row;
    const isUnderRow = item?.type === 'underRow';

    const props = {};
    if (row && !isUnderRow) {
        props.className = getRowClassName(row, rowIndex);
        props.onClick = e => handleRowClick(e, row, rowIndex, onRowClick);
        props.style = getRowStyle(onRowClick, row);
    }

    return <tr {...rest} {...props} />;
};
VirtuosoTableRow.propTypes = {
    context: PropTypes.shape({
        data: PropTypes.arrayOf(
            PropTypes.shape({
                type: PropTypes.string,
                row: PropTypes.shape({ disabled: PropTypes.bool }),
            })
        ),
    }),
    getRowClassName: PropTypes.func,
    onRowClick: PropTypes.func,
};

// Module-scope Virtuoso component definitions — must NOT be defined inside Table's render
// body, or Virtuoso treats them as new types on every render (react/no-unstable-nested-components).
// Values that vary per instance are threaded through the `context` prop on TableVirtuoso
// and consumed from `context` inside each component.

const VirtuosoTable = ({ context, ...props }) => <table {...props} className={context?.tableClassName} />;
VirtuosoTable.propTypes = { context: PropTypes.object };

const VirtuosoTableHead = ({ context, ...props }) => <thead {...props} className={context?.theadClass} />;
VirtuosoTableHead.propTypes = { context: PropTypes.object };

const VirtuosoTableBody = ({ context, ...props }) => <tbody {...props} className={context?.tbodyClass} />;
VirtuosoTableBody.propTypes = { context: PropTypes.object };

const VirtuosoTableRowCtx = ({ context, ...props }) => (
    <VirtuosoTableRow {...props} getRowClassName={context?.getRowClassName} onRowClick={context?.onRowClick} />
);
VirtuosoTableRowCtx.propTypes = { context: PropTypes.object };

const VIRTUOSO_COMPONENTS = {
    Table: VirtuosoTable,
    TableHead: VirtuosoTableHead,
    TableBody: VirtuosoTableBody,
    TableRow: VirtuosoTableRowCtx,
};

const getJustifyContent = headerAlign => {
    if (headerAlign === 'center') {
        return headerAlign;
    }

    return `flex-${headerAlign === 'right' ? 'end' : 'start'}`;
};

const getColumnTooltipProp = (tooltip, tooltipData) => {
    // There was a bug where this originally used `title` instead of `titleTranslationKey`
    // But some uses of this now pass an object, which can't be translated.. so this is a backwards-compatibility fix
    return typeof tooltip === 'string'
        ? { titleTranslationKey: tooltip, titleTranslationData: tooltipData }
        : { title: tooltip };
};

const Table = ({
    metadata = DEFAULT_METADATA,
    data = DEFAULT_DATA,
    tableClass = '',
    thStyle = {},
    dataTestId = '',
    actionComponentsConfigs = [],
    theadClass = '',
    thFiltersClass = '',
    thClass = '',
    tbodyClass = '',
    trClass = '',
    highlightRowIndex = -1,
    zebraTable = true,
    selectable = false,
    filterable = false,
    onToggleSelectAll = () => {},
    onToggleSingleRow = () => {},
    onRowClick = null,
    rowHoverEnabled = true,
    showInnerBorder = true,
    hasActionComponentCell = false,
    roundTopCorners = false,
    roundBottomCorners = false,
    emptyState = null,
    virtualized = false,
    pagination = null,
    groups = null,
}) => {
    const tableFilters = {};
    const [filteredData, setFilteredData] = useState(data);
    const totalColumns = metadata.length + (selectable ? 1 : 0) + (actionComponentsConfigs.length > 0 ? 1 : 0);
    const showEmptyState = Boolean(emptyState) && data.length === 0;

    useEffect(() => {
        setFilteredData(data);
    }, [data]);

    const isAllRowsSelected = () => {
        return data.every(row => row.is_selected);
    };

    const filterRows = (columnNameToFilter, filterText) => {
        tableFilters[columnNameToFilter] = filterText;

        let newFilteredData = filteredData;
        Object.keys(tableFilters).forEach(columnName => {
            newFilteredData = newFilteredData.filter(row => {
                let cellValue = row[columnName];
                if (typeof row[columnName] === 'object' && row[columnName] !== null) {
                    cellValue = row[columnName].name;
                }
                return cellValue.toLowerCase().includes(tableFilters[columnName]);
            });
        });

        setFilteredData(newFilteredData);
    };

    const renderCell = (row, col, rowIndex) => {
        const CellComponent = col.getCellComponent?.(row) || col.cellComponent || TableCell;
        const cellValues = {};
        if (col.cellValues) {
            col.cellValues.forEach(cellValue => {
                if (typeof cellValue === 'object') {
                    const { propName, dataKey, transformation } = cellValue;
                    cellValues[propName] = row[dataKey];
                    if (transformation) {
                        cellValues[propName] = transformation(cellValues[propName], rowIndex);
                    }
                } else {
                    cellValues[cellValue] = row[cellValue];
                }
            });
        }

        const ChildrenComponent = col.childrenComponent || null;
        const childrenComponentProps = {};
        if (col.childrenComponentProps) {
            col.childrenComponentProps.forEach(prop => {
                childrenComponentProps[prop.propName] = row[prop.dataKey];
            });
        }

        return ChildrenComponent ? (
            <CellComponent
                key={(row.key || rowIndex) + (col.key || col.displayName)}
                id={row.key || rowIndex}
                rowIndex={rowIndex}
                value={row[col.name]}
                onChange={value => onToggleSingleRow(value, row, col.name, rowIndex)}
                className={col.className}
                row={row}
                disabled={row.disabled || col.disabled}
                {...cellValues}
                {...col.cellEvents}
                {...col.cellProps}
                {...(row._isSkeleton ? { isLoading: true } : null)}
            >
                <ChildrenComponent {...childrenComponentProps} />
            </CellComponent>
        ) : (
            <CellComponent
                key={(row.key || rowIndex) + (col.key || col.displayName)}
                id={row.key || rowIndex}
                rowIndex={rowIndex}
                value={row[col.name]}
                subValue={row[col.subValue]}
                isBold={col.isBold}
                onChange={value => onToggleSingleRow(value, row, col.name, rowIndex)}
                className={col.className}
                row={row}
                disabled={row.disabled || col.disabled}
                {...cellValues}
                {...col.cellEvents}
                {...col.cellProps}
                {...(row._isSkeleton ? { isLoading: true } : null)}
            />
        );
    };

    const renderActionsCell = (row, rowIndex) => (
        <td className={css.actions} key={`${row.key || rowIndex}_actions`}>
            <div>
                {actionComponentsConfigs.map(
                    ({
                        ActionComponent,
                        onClick,
                        onMouseEnter,
                        onMouseLeave,
                        key,
                        title,
                        shouldShow = () => true,
                        tooltipText,
                        badge,
                        shouldTranslateToolTip,
                        className,
                    }) =>
                        shouldShow(row) ? (
                            <Tooltip
                                open={tooltipText ? null : false}
                                title={tooltipText}
                                titleTranslationKey={shouldTranslateToolTip ? tooltipText : null}
                                key={`tooltip_${key}`}
                                wrapperClass={css.action}
                            >
                                {badge && <span className={css.badge}>{badge}</span>}
                                <ActionComponent
                                    title={title}
                                    key={`${row.key || rowIndex}_${key}_actions`}
                                    className={className}
                                    onClick={() => onClick(row)}
                                    onMouseEnter={onMouseEnter}
                                    onMouseLeave={onMouseLeave}
                                />
                            </Tooltip>
                        ) : null
                )}
            </div>
        </td>
    );

    const renderHeaderRow = () => (
        <tr>
            {selectable && (
                <th className={thClass} style={{ width: '50px' }}>
                    <div className={css.checkboxCell}>
                        <Checkbox
                            checked={isAllRowsSelected()}
                            onChange={checked => onToggleSelectAll(checked)}
                            style={{ margin: 0 }}
                            checkmarkStyle={{ margin: 0 }}
                        />
                    </div>
                </th>
            )}
            {metadata
                .filter(({ subHeader }) => !subHeader)
                .map(col => (
                    <th
                        key={`col_header_${col.key || col.displayName}`}
                        style={{
                            textAlign: col.headerAlign || DEFAULT_HEADER_ALIGNMENT,
                            width: col.headerWidth,
                            fontWeight: col.boldHeader ? 'bold' : '',
                            ...thStyle,
                            ...(col.thStyle || {}),
                        }}
                        className={classNames(col.className, thClass, {
                            [css.actionsTh]: hasActionComponentCell,
                        })}
                        onClick={() => col.headerClick?.(col?.name)}
                    >
                        <div
                            className={css.tableHeaderContainer}
                            style={{ justifyContent: getJustifyContent(col.headerAlign) }}
                        >
                            {col.HeaderIcon && <col.HeaderIcon className={css.tableHeaderIcon} />}
                            <Translate className={css.tableHeaderText} id={col.displayName} />
                            {col.headerTooltip && (
                                <Tooltip
                                    {...getColumnTooltipProp(col.headerTooltip || '', col.headerTooltipData)}
                                    style={{ alignSelf: 'center' }}
                                >
                                    <Icon
                                        type={IconTypes.MINGCUTE}
                                        name="question-line"
                                        size={IconSizes.X_SMALL}
                                        variant={IconVariants.LIGHT}
                                        className={css.tableHeaderTooltipIcon}
                                    />
                                </Tooltip>
                            )}
                            {col.showHeaderArrow ? (
                                <div className={css.tableHeaderArrowContainer}>
                                    {col.headerArrow ? (
                                        col.headerArrow === 'up' ? (
                                            <SortUp className={css.arrowIcon} />
                                        ) : (
                                            <SortDown className={css.arrowIcon} />
                                        )
                                    ) : (
                                        <SortOff className={css.sortOff} />
                                    )}
                                </div>
                            ) : null}
                        </div>
                    </th>
                ))}
        </tr>
    );

    const renderSelectableCell = row => (
        <td key={`${row.key}_selectable`} style={{ width: '50px' }}>
            <div className={css.checkboxCell}>
                <Checkbox
                    checked={row.is_selected}
                    onChange={checked => onToggleSingleRow(checked, row)}
                    style={{ margin: 0 }}
                    checkmarkStyle={{ margin: 0 }}
                    disabled={row.disabled}
                />
            </div>
        </td>
    );

    const renderRowCells = (row, rowIndex) => {
        const cells = [];

        if (selectable) {
            cells.push(renderSelectableCell(row));
        }

        cells.push(
            ...metadata
                .filter(({ subHeader }) => (row.subHeaderText ? subHeader : !subHeader))
                .map(col => renderCell(row, col, rowIndex))
        );

        if (actionComponentsConfigs.length > 0) {
            cells.push(renderActionsCell(row, rowIndex));
        }

        return cells;
    };

    const renderFilterRow = () =>
        filterable && (
            <tr>
                {selectable && <th className={thFiltersClass} />}
                {metadata.map(col => (
                    <th key={`col_filter_${col.displayName}`} className={classNames(css.filterCell, thFiltersClass)}>
                        <TextField
                            placeholder={`Search ${col.placeholder}`}
                            onChange={text => filterRows(col.name, text)}
                        />
                    </th>
                ))}
            </tr>
        );

    const renderUnderRowContent = (row, rowIndex) => (
        <td className={css.underRowCol} colSpan={totalColumns}>
            {typeof row.underRowComponent === 'function' ? row.underRowComponent(row, rowIndex) : row.underRowComponent}
        </td>
    );

    const tableClassName = classNames(
        css.table,
        tableClass,
        { [css.tableWithRowHover]: rowHoverEnabled && !showEmptyState },
        { [css.showInnerBorder]: showInnerBorder },
        { [css.roundTopCorners]: roundTopCorners },
        { [css.roundBottomCorners]: roundBottomCorners }
    );

    const getRowClassName = (row, rowIndex) =>
        classNames(
            { [css.highlightRow]: rowIndex === highlightRowIndex },
            { [css.rowActionsHover]: (hasActionComponentCell || actionComponentsConfigs.length) && !row.disabled },
            { [css.zebraRowEven]: zebraTable && rowIndex % 2 === 1 },
            { [css.disabledRow]: row.disabled },
            row.trClass,
            trClass
        );

    // Renders a single data row as a <tr> (used in both grouped and flat paths).
    const renderRow = (row, rowIndex) => (
        <React.Fragment key={row.key || rowIndex}>
            <tr
                className={getRowClassName(row, rowIndex)}
                onClick={e => handleRowClick(e, row, rowIndex, onRowClick)}
                style={getRowStyle(onRowClick, row)}
            >
                {renderRowCells(row, rowIndex)}
            </tr>
            {row.underRowComponent && <tr>{renderUnderRowContent(row, rowIndex)}</tr>}
        </React.Fragment>
    );

    if (groups) {
        return (
            <table data-testid={dataTestId} className={tableClassName}>
                <thead className={theadClass}>
                    {renderHeaderRow()}
                    {renderFilterRow()}
                </thead>
                <tbody className={tbodyClass}>
                    {groups.map(group => (
                        <React.Fragment key={group.key}>
                            <tr key={`header_${group.key}`}>
                                <GroupHeaderCell
                                    row={{
                                        groupTitle: group.label,
                                        isCollapsed: !!group.isCollapsed,
                                        onToggle: group.onToggleCollapse,
                                    }}
                                    colSpan={totalColumns}
                                />
                            </tr>
                            {!group.isCollapsed && group.data.length === 0 && !group.pagination?.isLoading && (
                                <tr key={`empty_${group.key}`}>
                                    <GroupHeaderCell
                                        row={{
                                            isEmptyState: true,
                                            emptyStateComponent: <Translate id="STATIC.PAGINATION.EMPTY_GROUP" />,
                                        }}
                                        colSpan={totalColumns}
                                    />
                                </tr>
                            )}
                            {!group.isCollapsed && group.data.map((row, idx) => renderRow(row, idx))}
                            {!group.isCollapsed && group.pagination && (
                                <tr>
                                    <td colSpan={totalColumns} style={{ padding: 0 }}>
                                        <PaginationFooter
                                            offset={group.pagination.offset}
                                            limit={group.pagination.limit}
                                            total={group.pagination.total}
                                            isLoading={group.pagination.isLoading}
                                            itemsShown={group.data.length}
                                            onPageChange={group.pagination.onPageChange}
                                        />
                                    </td>
                                </tr>
                            )}
                        </React.Fragment>
                    ))}
                </tbody>
            </table>
        );
    }

    // Virtualized table rendering
    if (virtualized && !showEmptyState) {
        // Flatten data to include under-row components as separate rows
        const virtualizedData = [];
        filteredData.forEach((row, index) => {
            virtualizedData.push({ type: 'row', row, index });
            if (row.underRowComponent) {
                virtualizedData.push({ type: 'underRow', row, index });
            }
        });

        return (
            <TableVirtuoso
                data-testid={dataTestId}
                data={virtualizedData}
                context={{ data: virtualizedData, tableClassName, theadClass, tbodyClass, getRowClassName, onRowClick }}
                className={css.virtualizedTableScroller}
                components={VIRTUOSO_COMPONENTS}
                computeItemKey={(_, item) => `${item.type}_${item.row?.key || item.index}`}
                fixedHeaderContent={() => (
                    <>
                        {renderHeaderRow()}
                        {renderFilterRow()}
                    </>
                )}
                itemContent={(_, item) =>
                    item.type === 'underRow'
                        ? renderUnderRowContent(item.row, item.index)
                        : renderRowCells(item.row, item.index)
                }
                endReached={() => {
                    if (pagination && pagination.onLoadMore && pagination.hasMore && !pagination.isLoading) {
                        pagination.onLoadMore();
                    }
                }}
            />
        );
    }

    return (
        <table data-testid={dataTestId} className={tableClassName}>
            <thead className={theadClass}>
                {renderHeaderRow()}
                {renderFilterRow()}
            </thead>
            <tbody className={tbodyClass}>
                {showEmptyState && (
                    <tr>
                        <td colSpan={totalColumns} className={css.emptyState}>
                            {emptyState}
                        </td>
                    </tr>
                )}
                {filteredData.map((row, i) => (
                    <React.Fragment key={row.key || i}>
                        <tr
                            className={getRowClassName(row, i)}
                            onClick={e => handleRowClick(e, row, i, onRowClick)}
                            style={getRowStyle(onRowClick, row)}
                        >
                            {renderRowCells(row, i)}
                        </tr>
                        {row.underRowComponent && <tr>{renderUnderRowContent(row, i)}</tr>}
                    </React.Fragment>
                ))}
                {pagination && (
                    <tr>
                        <td colSpan={totalColumns} style={{ padding: 0 }}>
                            <PaginationFooter
                                offset={pagination.offset}
                                limit={pagination.limit}
                                total={pagination.total}
                                isLoading={pagination.isLoading}
                                onPageChange={pagination.onPageChange}
                            />
                        </td>
                    </tr>
                )}
            </tbody>
        </table>
    );
};

Table.propTypes = {
    metadata: TableWidgetMetadataShape,
    data: PropTypes.arrayOf(PropTypes.any),
    actionComponentsConfigs: PropTypes.arrayOf(PropTypes.any),
    tableClass: PropTypes.string,
    thStyle: PropTypes.objectOf(PropTypes.any),
    theadClass: PropTypes.string,
    thClass: PropTypes.string,
    thFiltersClass: PropTypes.string,
    tbodyClass: PropTypes.string,
    trClass: PropTypes.string,
    rowHoverEnabled: PropTypes.bool,
    highlightRowIndex: PropTypes.number,
    zebraTable: PropTypes.bool,
    selectable: PropTypes.bool,
    filterable: PropTypes.bool,
    onToggleSelectAll: PropTypes.func,
    onToggleSingleRow: PropTypes.func,
    onRowClick: PropTypes.func,
    translate: PropTypes.func,
    dataTestId: PropTypes.string,
    showInnerBorder: PropTypes.bool,
    hasActionComponentCell: PropTypes.bool,
    roundTopCorners: PropTypes.bool,
    roundBottomCorners: PropTypes.bool,
    emptyState: PropTypes.element,
    virtualized: PropTypes.bool,
    pagination: PropTypes.shape({
        offset: PropTypes.number.isRequired,
        limit: PropTypes.number.isRequired,
        total: PropTypes.number.isRequired,
        hasMore: PropTypes.bool,
        isLoading: PropTypes.bool,
        onPageChange: PropTypes.func,
        onLoadMore: PropTypes.func,
        pageSizeOptions: PropTypes.arrayOf(PropTypes.number),
        onPageSizeChange: PropTypes.func,
    }),
    groups: PropTypes.arrayOf(
        PropTypes.shape({
            key: PropTypes.string.isRequired,
            label: PropTypes.node.isRequired,
            data: PropTypes.array.isRequired,
            isCollapsed: PropTypes.bool,
            onToggleCollapse: PropTypes.func.isRequired,
            pagination: PropTypes.shape({
                offset: PropTypes.number.isRequired,
                limit: PropTypes.number.isRequired,
                total: PropTypes.number.isRequired,
                hasMore: PropTypes.bool,
                isLoading: PropTypes.bool,
                onPageChange: PropTypes.func,
                onLoadMore: PropTypes.func,
            }),
            headerExtras: PropTypes.node,
        })
    ),
};

export default Table;
